import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { checkEnableCompanySupport } from 'utils/helpers/featureFlags';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

const getDisciplinaryActions = async ({ cohortId, status }: { cohortId: number; status: string }) => {
  try {
    const url = endpoints.disciplinaryActions.getDisciplinaryActions;

    const response = await sendRequest({
      url,
      method: 'GET',
      credentials: 'include',
      queryParams: { cohortId, status },
    });
    return response;
  } catch (e) {
    console.error('Error failed to get disciplinary actions', e);
    throw e;
  }
};

const getDisciplinaryActionsNotesChartData = async ({
  graphType,
  cohortId,
  reportType,
}: {
  cohortId: number;
  graphType: string;
  reportType: string;
}) => {
  try {
    const url = endpoints.disciplinaryActions.getDisciplinaryActionsGraph;

    const response = await sendRequest({
      url,
      method: 'POST',
      credentials: 'include',
      queryParams: { graphType },
      body: {
        cohortId,
        reportType,
        rowGroupCols: [],
        pivotCols: [],
        pivotMode: false,
        filterModel: {},
        baseFilter: {},
        limit: null,
        sortModel: [],
      },
    });
    return response;
  } catch (e) {
    console.error('Error failed to get disciplinary actions chart data', e);
    throw e;
  }
};

const setDisciplinaryActionsDefaultValues = (row) => {
  const newRow = { ...row };

  if (!newRow.COMPANY) {
    newRow.COMPANY = { id: 'NA', companyName: 'NA' };
  }
  if (!newRow.LOCATION) {
    newRow.LOCATION = 'NA';
  }
  return newRow;
};

export const useGetDisciplinaryActions = ({ cohortId, status }: { cohortId: number; status: string }) =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.disciplinaryActions.getDisciplinaryActions, cohortId, status],
    queryFn: () =>
      getDisciplinaryActions({ cohortId, status }).then((res) => {
        if (checkEnableCompanySupport()) {
          const updatedRes = { ...res, rows: res.rows.map(setDisciplinaryActionsDefaultValues) };
          return updatedRes;
        }

        return res;
      }),
    enabled: !!cohortId,
  });

export const useGetDisciplinaryActionsNotesCountChartData = ({
  graphType,
  cohortId,
  reportType,
}: {
  cohortId: number;
  graphType: string;
  reportType: string;
}) =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.disciplinaryActions.getDisciplinaryActionsNotesCountChartData],
    queryFn: () => getDisciplinaryActionsNotesChartData({ graphType, cohortId, reportType }),
    enabled: !!cohortId,
  });

export const useGetDisciplinaryActionsNotesStageChartData = ({
  graphType,
  cohortId,
  reportType,
}: {
  cohortId: number;
  graphType: string;
  reportType: string;
}) =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.disciplinaryActions.getDisciplinaryActionsNotesStageChartData],
    queryFn: () => getDisciplinaryActionsNotesChartData({ graphType, cohortId, reportType }),
    enabled: !!cohortId,
  });

export const useInvalidateGetDisciplinaryActions = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.disciplinaryActions.getDisciplinaryActions]);
  };
};

const publishDisciplinaryNote = async ({ noteId }: { noteId: number }) => {
  try {
    const url = endpoints.disciplinaryActions.publishDisciplinaryNote(noteId);

    const response = await sendRequest({
      url,
      method: 'PATCH',
      credentials: 'include',
    });
    return response;
  } catch (e) {
    console.error('Error failed to publish disciplinary note', e);
    throw e;
  }
};

export const usePublishDisciplinaryNote = () =>
  useMutation({
    mutationFn: ({ noteId }: { noteId: number }) => publishDisciplinaryNote({ noteId }),
  });

const bulkPublishDisciplinaryNotes = async ({ noteIds }: { noteIds: number[] }) => {
  try {
    const url = endpoints.disciplinaryActions.bulkPublishDisciplinaryNotes;

    const response = await sendRequest({
      url,
      method: 'PATCH',
      credentials: 'include',
      body: {
        noteIds,
      },
    });
    return response;
  } catch (e) {
    console.error('Error failed to bulk publish disciplinary notes', e);
    throw e;
  }
};

export const useBulkPublishDisciplinaryNotes = () =>
  useMutation({
    mutationFn: ({ noteIds }: { noteIds: number[] }) => bulkPublishDisciplinaryNotes({ noteIds }),
  });

export const getAdminReportsApi = ({
  cohortId,
  limit = 1000,
  pivotMode = false,
  rowGroupCols = [],
  pivotCols = [],
  filterModel = {},
  baseFilter = {},
}) => {
  const url = endpoints.disciplinaryActions.generateReport;
  if (!cohortId) {
    return Promise.resolve({ entity: { data: [] } });
  }
  const res = sendRequest({
    url,
    method: 'POST',
    credentials: 'include',
    body: {
      reportType: 'DISCIPLINARY_ACTION_ADMIN_REPORT',
      cohortId,
      limit,
      baseFilter,
      pivotMode,
      rowGroupCols,
      pivotCols,
      filterModel,
    },
  });

  return res;
};

export const useGetAdminReports = ({
  cohortId,
  limit = 1000,
  pivotMode = false,
  rowGroupCols = [],
  pivotCols = [],
  filterModel = {},
  baseFilter,
}) =>
  useQuery({
    queryKey: [
      REACT_QUERY_KEYS.disciplinaryActions.getAdminReports,
      cohortId,
      baseFilter.startDate,
      baseFilter.endDate,
    ],
    queryFn: () =>
      getAdminReportsApi({ cohortId, limit, pivotMode, rowGroupCols, pivotCols, filterModel, baseFilter }).then(
        (res) => {
          if (checkEnableCompanySupport()) {
            const data = res?.entity || {};
            const updatedRes = { ...data, rows: data.rows.map(setDisciplinaryActionsDefaultValues) };
            return { entity: updatedRes };
          }

          return res;
        }
      ),
    enabled: !!cohortId,
  });

export const getAdminReportsColumnsApi = () => {
  const url = endpoints.disciplinaryActions.getReportColumns;
  return sendRequest({
    url,
    method: 'GET',
    credentials: 'include',
    queryParams: { reportType: 'DISCIPLINARY_ACTION_ADMIN_REPORT' },
  });
};

export const useGetAdminReportsColumns = () =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.disciplinaryActions.getAdminReportsColumns],
    queryFn: () => getAdminReportsColumnsApi(),
  });

const deleteViolationNoteApi = async ({ noteId }: { noteId: number }) => {
  try {
    const url = endpoints.disciplinaryActions.deleteViolationNote(noteId);

    const response = await sendRequest({
      url,
      method: 'DELETE',
      credentials: 'include',
    });
    return response;
  } catch (e) {
    console.error('Error failed to delete disciplinary note', e);
    throw e;
  }
};

export const useDeleteViolationNote = () =>
  useMutation({
    mutationFn: ({ noteId }: { noteId: number }) => deleteViolationNoteApi({ noteId }),
  });

const getViolationTypesApi = async () => {
  try {
    const url = endpoints.disciplinaryActions.getViolationTypes;
    const response = await sendRequest({
      url,
      method: 'GET',
      credentials: 'include',
    });
    return response;
  } catch (e) {
    console.error('Error failed to get violation types', e);
    throw e;
  }
};

export const useGetViolationTypes = () =>
  useQuery({
    queryKey: [REACT_QUERY_KEYS.disciplinaryActions.getViolationTypes],
    queryFn: () => getViolationTypesApi(),
  });
